/**
 * v1.4.45 — Manual UI zoom / screen size.
 *
 * Applies a CSS `zoom` on the document root so the WHOLE app scales
 * uniformly. Independent of the tile-size preset — this is a
 * broader "make the whole app bigger or smaller" control for TVs
 * whose native display density is off (over-scan, under-scan, weird
 * DPI, remote-controlled zoom, etc).
 *
 * Persisted in localStorage under `tv_player.uiScale` as a decimal
 * (0.7 = 70%, 1.0 = 100%, 1.3 = 130%). Applied on app boot and
 * whenever setUiScale() is called.
 */
const KEY = "tv_player.uiScale";
const ENABLED_KEY = "tv_player.uiScaleEnabled";
const MIN = 0.7;
const MAX = 1.4;
const DEFAULT = 1.0;

/**
 * v1.4.46 — Whether the user has opted-in to custom scaling. When
 * FALSE, applyUiScale() always resets to 1.0 regardless of the
 * saved scale value. Lets users toggle scaling on/off without
 * losing their preferred percentage.
 */
export function isUiScaleEnabled() {
  try { return localStorage.getItem(ENABLED_KEY) === "1"; }
  catch { return false; }
}

export function setUiScaleEnabled(on) {
  try { localStorage.setItem(ENABLED_KEY, on ? "1" : "0"); }
  catch { /* SSR / quota */ }
  applyUiScale(on ? getUiScale() : DEFAULT);
  try {
    window.dispatchEvent(new CustomEvent("tv_player:uiScaleEnabled", { detail: !!on }));
  } catch { /* SSR */ }
}

/**
 * v1.4.46 — Compute a viewport-optimal scale. Aims for a design
 * target of 1080px viewport height; any TV reporting bigger or
 * smaller is nudged proportionally, clamped to [MIN, MAX].
 */
export function bestFitScale() {
  if (typeof window === "undefined") return DEFAULT;
  const h = window.innerHeight || 1080;
  const raw = h / 1080;
  return Math.max(MIN, Math.min(MAX, Math.round(raw * 20) / 20));
}

export function getUiScale() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw == null) return DEFAULT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT;
    return Math.max(MIN, Math.min(MAX, n));
  } catch {
    return DEFAULT;
  }
}

export function setUiScale(v) {
  const clamped = Math.max(MIN, Math.min(MAX, Number(v) || DEFAULT));
  try {
    localStorage.setItem(KEY, String(clamped));
  } catch { /* SSR / quota */ }
  // Only actually APPLY the scale when the enable toggle is on.
  // Otherwise the value is just persisted for later.
  if (isUiScaleEnabled()) {
    applyUiScale(clamped);
  }
  try {
    window.dispatchEvent(new CustomEvent("tv_player:uiScale", { detail: clamped }));
  } catch { /* SSR */ }
  return clamped;
}

/**
 * Applies the given scale to the DOM. Prefers CSS `zoom` (which
 * REFLOWS the layout — click targets, scroll, focus rings all scale
 * naturally) over `transform: scale()` (which just visually rescales
 * without affecting layout, so scrollbars and clicks misalign).
 * Chromium and Safari support `zoom`; Firefox added it in 2024.
 */
export function applyUiScale(scale) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  if (!el) return;
  el.style.zoom = String(scale);
}

// Boot-time apply — called once from App.jsx / index.jsx so the
// setting persists across page reloads. Respects the on/off toggle;
// no-op (scale = 1.0) when the user has not enabled custom scaling.
export function initUiScale() {
  applyUiScale(isUiScaleEnabled() ? getUiScale() : DEFAULT);
}

export const UI_SCALE_MIN = MIN;
export const UI_SCALE_MAX = MAX;
export const UI_SCALE_DEFAULT = DEFAULT;
